import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from "@datadog/browser-logs";
import pkg from "../../package.json";

const site = "datadoghq.com";
const service = "infinitekitchenonline";
const clientToken = process.env.REACT_APP_DD_CLIENT_TOKEN ?? "";

/**
 * @description Initializes the datadog RUM package with interaction tracking. The app_key and client_token
 * must be set within the config file. Each app must have a unique client token generated
 * on the {@link https://app.datadoghq.com/rum/create|datadog website}. These tokens must then be added as github repo secrets.
 * See the datadog docs for more info {@link https://docs.datadoghq.com/real_user_monitoring/browser/#npm}
 */
export function initDatadog(): void {
	datadogRum.init({
		applicationId: process.env.REACT_APP_DD_APP_KEY ?? "",
		clientToken,
		site,
		service,
		sampleRate: 100,
		env: process.env.REACT_APP_ENV,
		version: pkg.version,
		trackInteractions: true,
	});
	datadogLogs.init({
		clientToken,
		site,
		service,
		sampleRate: 100,
		env: process.env.REACT_APP_ENV,
		version: pkg.version,
		forwardErrorsToLogs: true,
	});
}

/**
 * @description Reports a custom page view using a custom user action.
 * Since react apps are often single page, this can be used to track menu visits and modal activations.
 * {@link https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/?tab=npm#custom-user-actions}
 * @param string page Name of the page or menu that has been visited.
 */
export function reportPageView(page: string): void {
	datadogRum.addUserAction("pageview", { page: page });
	datadogLogs.logger.log("pageview", { page: page });
}

/**
 * @description Reports a custom error not covered by automatic tracking provided by datadog RUM
 * {@link https://docs.datadoghq.com/real_user_monitoring/browser/collecting_browser_errors/?tab=npm#collect-errors-manually}
 * @param string error Description of the error encountered
 */
export function reportError(error: string): void {
	datadogRum.addError(new Error(error), undefined, "custom");
}

/**
 * @description Report a custom user action.
 * Provide a name and context object containing any additional data
 * {@link https://docs.datadoghq.com/real_user_monitoring/android/?tab=us#manual-tracking}
 * @param string name The name of the custom event
 * @param object context Additional data to include with this event
 * @param boolean logEvent should this event be archived using datadog logs
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export function reportEvent(name: string, context?: object, logEvent = true): void {
	datadogRum.addAction(name, context);
	if (logEvent) datadogLogs.logger.log(name, context, "info");
}
